import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Shield } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import type { OrganizationSettings } from '../hooks/useSettings';

interface SecuritySettingsProps {
  settings: OrganizationSettings['security'] | null;
  isLoading: boolean;
  onSave: (updates: Partial<OrganizationSettings['security']>) => Promise<boolean | undefined>;
}

const SESSION_TIMEOUTS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 480, label: '8 hours' },
  { value: 1440, label: '24 hours' },
];

export function SecuritySettings({ settings, isLoading, onSave }: SecuritySettingsProps) {
  const { t } = useTranslation();
  const [requireTwoFactor, setRequireTwoFactor] = useState(settings?.requireTwoFactor || false);
  const [sessionTimeout, setSessionTimeout] = useState(settings?.sessionTimeoutMinutes || 60);
  const [ipAddresses, setIpAddresses] = useState(settings?.allowedIpAddresses?.join(', ') || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setRequireTwoFactor(settings.requireTwoFactor);
      setSessionTimeout(settings.sessionTimeoutMinutes);
      setIpAddresses(settings.allowedIpAddresses?.join(', ') || '');
    }
  }, [settings]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const allowedIpAddresses = ipAddresses
        .split(',')
        .map((ip) => ip.trim())
        .filter((ip) => ip.length > 0);

      await onSave({
        requireTwoFactor,
        sessionTimeoutMinutes: sessionTimeout,
        allowedIpAddresses,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          {t('settings.security')}
        </CardTitle>
        <CardDescription>{t('settings.securityDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Two-Factor Authentication */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <label className="text-sm font-medium">{t('settings.twoFactor')}</label>
            <p className="text-sm text-muted-foreground">{t('settings.twoFactorDescription')}</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={requireTwoFactor}
              onChange={(e) => setRequireTwoFactor(e.target.checked)}
              className="sr-only peer"
              disabled={isLoading}
            />
            <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        {/* Session Timeout */}
        <div className="space-y-2">
          <label htmlFor="sessionTimeout" className="text-sm font-medium">
            {t('settings.sessionTimeout')}
          </label>
          <p className="text-sm text-muted-foreground">{t('settings.sessionTimeoutDescription')}</p>
          <select
            id="sessionTimeout"
            value={sessionTimeout}
            onChange={(e) => setSessionTimeout(Number(e.target.value))}
            className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
            disabled={isLoading}
          >
            {SESSION_TIMEOUTS.map((timeout) => (
              <option key={timeout.value} value={timeout.value}>
                {timeout.label}
              </option>
            ))}
          </select>
        </div>

        {/* IP Allowlist */}
        <div className="space-y-2">
          <label htmlFor="ipAllowlist" className="text-sm font-medium">
            {t('settings.ipAllowlist')}
          </label>
          <p className="text-sm text-muted-foreground">{t('settings.ipAllowlistDescription')}</p>
          <Input
            id="ipAllowlist"
            value={ipAddresses}
            onChange={(e) => setIpAddresses(e.target.value)}
            placeholder="192.168.1.0/24, 10.0.0.0/8"
            disabled={isLoading}
          />
          <p className="text-xs text-muted-foreground">{t('settings.ipAllowlistHint')}</p>
        </div>

        <Button onClick={handleSave} disabled={isLoading || isSaving}>
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('common.save')}
        </Button>
      </CardContent>
    </Card>
  );
}
